/**
 * EMR toast notifications: show on EMR page load until user closes.
 * Add entries to EMR_MESSAGES to show new messages; use emrs to limit by EMR.
 * Uses cookies for dismissal (no manifest permission needed; harder to clear than localStorage).
 */

import { showBanner } from '../utils/toast.js';

const DISMISS_KEY_PREFIX = 'comprehendpt_emr_msg_';
const DISMISS_COOKIE_DAYS = 365;

/** EMR injector keys (from DOMEvaluator injectors) for use in emrs filter: e.g. 'app.webpt.com', 'janeapp', 'mwtherapy.com', 'athenahealth.com', etc. */
export type EMRKey = string;

export interface EMRMessage {
  /** Unique id; used for cookie dismiss key so users can dismiss per message */
  id: string;
  /** HTML content (can include <a> etc.) */
  content: string;
  /** 'all' = every EMR; or list of injector keys to show only on those EMRs */
  emrs?: 'all' | EMRKey[];
  /**
   * ISO date string. If set, the message will not be shown after this date.
   * Example: '2026-04-22T23:59:59Z' to show only until April 22 2026 end-of-day UTC.
   */
  showUntil?: string;
}

/**
 * List of EMR messages. Add new items here; each can be scoped by emrs.
 * First matching, non-dismissed, non-expired message is shown per page load.
 * NOTE: Clean these up once everyone has seen them — they'll be stale.
 */
export const EMR_MESSAGES: EMRMessage[] = [
  // Example: WebPT-only message:
  // { id: 'webpt-tip', content: 'Tip for WebPT users...', emrs: ['app.webpt.com'],   //   showUntil: '2026-04-22T23:59:59Z' },
  // {
  //   id: 'webinar',
  //   content: `Learn more about Comprehend. Attend webinar, <a href="https://www.comprehendpt.com/webinar" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 600;">click here to register</a>`,
  //   emrs: 'all'
  // },
  // { // BE CAREFUL! Word from CORA users is these aren't actually dismissing... they come back every 2 hours??? Maybe Saphire is doing somethign with the local storage?
  //   id: 'cora_webinar_feb',
  //   content: `Stop taking documentation home with you. Learn how to fully leverage Comprehend at CORA. <a href="https://coraphysicaltherapy.csod.com/ui/lms-learning-details/app/event/8ee21864-6b51-4ac8-95c8-aa93c0ff00a3" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 600;">Register here</a>.`,
  //   emrs: ['corahealth.com'],
  //   showUntil: '2026-04-22T23:59:59Z'
  // }
  // {
  //   id: 'empower_help',
  //   content: `Having trouble with the Empower EMR? Check out the <a href="https://www.comprehendpt.com/empower-help" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 600;">Empower Help Center</a>`,
  //   emrs: ['empoweremr.com'],
  //   showUntil: '2026-04-22T23:59:59Z'
  // }
];

function getDismissKey(id: string): string {
  return `${DISMISS_KEY_PREFIX}${id}`;
}

function getCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .find((c) => c.trim().startsWith(name + '='));
  return match ? match.trim().slice(name.length + 1) : null;
}

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

function isDismissed(dismissKey: string): boolean {
  return getCookie(dismissKey) === 'true';
}

function messageMatchesEMR(
  message: EMRMessage,
  emrKey: string | null
): boolean {
  if (!emrKey) return false;
  if (message.emrs === undefined || message.emrs === 'all') return true;
  return message.emrs.includes(emrKey);
}

function messageIsExpired(message: EMRMessage): boolean {
  if (!message.showUntil) return false;
  return new Date() > new Date(message.showUntil);
}

const LOGO_URL = 'https://app.comprehendpt.com/Logo.svg';

function showEMRToastsIfNeeded(getEMRKey: () => string | null) {
  try {
    const emrKey = getEMRKey();
    if (!emrKey) return;

    for (const message of EMR_MESSAGES) {
      if (!messageMatchesEMR(message, emrKey)) continue;
      if (messageIsExpired(message)) continue;

      const dismissKey = getDismissKey(message.id);
      if (isDismissed(dismissKey)) continue;

      showBanner({
        content: message.content,
        toastId: message.id,
        logoUrl: LOGO_URL,
        onDismiss: () => setCookie(dismissKey, 'true', DISMISS_COOKIE_DAYS)
      });
    }
  } catch (e) {
    console.warn('Comprehend EMR banner error:', e);
  }
}

/**
 * Initialize EMR toasts. Call once when the content script loads.
 * @param getEMRKey - Returns current EMR injector key or null (e.g. lookupInjectorKey from DOMEvaluator)
 */
export function initEMRBanner(getEMRKey: () => string | null) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () =>
      showEMRToastsIfNeeded(getEMRKey)
    );
  } else {
    showEMRToastsIfNeeded(getEMRKey);
  }
}
